import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import services from '../../../services';
import utils from '../../../utils';
import normalizarRespostaPayloadParaUpsert, {
  temValorResposta
} from '../_shared/avaliacaoRespostaPayload';
import hydrateAvaliacaoQuestoes from '../_shared/hydrateAvaliacaoQuestoes';
import { getAvaliacaoStatusId } from '../_shared/avaliacaoStatus';
import {
  wFluxoEntradaAvaliacao,
  wOtpGestorAcessoTamanho,
  wOtpGestorCooldownMs,
  wOtpGestorEtapas,
  wOtpGestorModos
} from './avaliacoesGestorEdit.constants';
import {
  agruparAnexos,
  extrairRespostasGestorPorAvaliacao,
  extrairValorResposta,
  formatarCooldownOtp,
  isErroAcessoNegado,
  normalizarOtp,
  resolverFluxoEntrada
} from './avaliacoesGestorEdit.helpers';

const pvCriarOtpStatusInicial = () => ({
  etapa: null,
  modo: wOtpGestorModos.idle,
  erro: '',
  cooldownEndsAt: null
});

const pvCriarOtpStatusVerificacaoInicial = () => ({
  etapa: wOtpGestorEtapas.verificar,
  modo: wOtpGestorModos.idle,
  erro: '',
  cooldownEndsAt: null
});

const wStatusEntregaAssinatura = {
  confirmacao: 'confirmacao',
  enviando: 'enviando'
};

const pvResolverFluxoEfetivo = (pStatus, pRequestOptions = {}) => {
  const xFluxo = resolverFluxoEntrada(pStatus);

  if (
    xFluxo === wFluxoEntradaAvaliacao.gate &&
    getAvaliacaoStatusId(pStatus) === 10 &&
    pRequestOptions?.authToken
  ) {
    return wFluxoEntradaAvaliacao.questionario;
  }

  return xFluxo;
};

export default function useAvaliacoesGestorEditController(pProps) {
  const {
    paramsId,
    locationSearch,
    state,
    actions,
    contextAvaliacao,
    hasResolvedAvaliacaoStatus,
    isLoadingAvaliacaoStatus,
    onRefreshAvaliacaoStatus,
    onOtpValidated,
    onMessageError,
    onMessageSuccess
  } = pProps;
  const { gestor, activeCategoryId, activeQuestionId } = state;

  const [anexosAvaliacao, setAnexosAvaliacao] = useState([]);
  const [respostasGestor, setRespostasGestor] = useState({});
  const [assinaturaDialogOpen, setAssinaturaDialogOpen] = useState(false);
  const [assinaturaConfirmada, setAssinaturaConfirmada] = useState(false);
  const [assinaturaEntregaStatus, setAssinaturaEntregaStatus] = useState(
    wStatusEntregaAssinatura.confirmacao
  );
  const [assinaturaEntregaErro, setAssinaturaEntregaErro] = useState('');
  const [mostrarApenasNaoRespondidas, setMostrarApenasNaoRespondidas] = useState(false);
  const [gruposQuestoesAbertos, setGruposQuestoesAbertos] = useState({});
  const [anexoRemocaoPendente, setAnexoRemocaoPendente] = useState(null);
  const [avaliacao, setAvaliacao] = useState(null);
  const [avaliacaoSalvando, setAvaliacaoSalvando] = useState(false);
  const [modeloQuestoes, setModeloQuestoes] = useState([]);
  const [fluxoEntrada, setFluxoEntrada] = useState(wFluxoEntradaAvaliacao.questionario);
  const [fluxoAcao, setFluxoAcao] = useState('idle');
  const [otpCodigo, setOtpCodigo] = useState('');
  const [otpStatus, setOtpStatus] = useState(pvCriarOtpStatusInicial);
  const [avaliacaoToken, setAvaliacaoToken] = useState('');
  const [telaCarregando, setTelaCarregando] = useState(false);

  const xInputAnexosRef = useRef(null);
  const xOtpInputRefs = useRef([]);
  const xDebouncedUpsertByQuestaoRef = useRef({});
  const xUpsertsPendentesRef = useRef(0);
  const xUltimaHidratacaoRef = useRef('');

  const xTemQuestoesDoModelo = modeloQuestoes.length > 0;
  const xTituloAvaliacao = avaliacao?.ativo?.ativo_seuid || gestor.pessoa_fantasia || null;
  const xDescricaoAvaliacao = avaliacao?.gestor?.pessoa_fantasia || '';
  const xAvaliacaoTokenRequestOptions = useMemo(
    () => (avaliacaoToken ? { authToken: avaliacaoToken } : {}),
    [avaliacaoToken]
  );
  const xHydratacao = useMemo(
    () => hydrateAvaliacaoQuestoes(avaliacao, modeloQuestoes),
    [avaliacao, modeloQuestoes]
  );
  const xOtpNecessario = Boolean(otpStatus.etapa);
  const xOtpQueryParam = useMemo(() => {
    const xSearchParams = new URLSearchParams(locationSearch || '');
    const xOtpNormalizado = normalizarOtp(xSearchParams.get('otp'));

    return xOtpNormalizado.length === wOtpGestorAcessoTamanho ? xOtpNormalizado : '';
  }, [locationSearch]);
  const xOtpDigitos = useMemo(
    () =>
      Array.from(
        { length: wOtpGestorAcessoTamanho },
        (_pItem, pIndex) => otpCodigo?.[pIndex] || ''
      ),
    [otpCodigo]
  );
  const xOtpCooldownRestanteMs = Math.max(0, Number(otpStatus.cooldownEndsAt || 0) - Date.now());
  const xOtpCooldownAtivo = xOtpCooldownRestanteMs > 0;
  const xOtpPodeValidar =
    otpCodigo.length === wOtpGestorAcessoTamanho && otpStatus.modo === wOtpGestorModos.idle;
  const xOtpPodeReenviar = otpStatus.modo === wOtpGestorModos.idle && !xOtpCooldownAtivo;
  const xFluxoAcaoEmAndamento = fluxoAcao !== 'idle';
  const xEntregaEmAndamento =
    assinaturaEntregaStatus === wStatusEntregaAssinatura.enviando;
  const xStatusContextoId = useMemo(() => {
    const xStatusNormalizado = getAvaliacaoStatusId(contextAvaliacao?.status);

    if (xStatusNormalizado !== null) {
      return xStatusNormalizado;
    }

    const xStatusNumerico = Number(contextAvaliacao?.status);

    return Number.isFinite(xStatusNumerico) ? xStatusNumerico : null;
  }, [contextAvaliacao]);
  const xFluxoContexto = useMemo(
    () => resolverFluxoEntrada(xStatusContextoId),
    [xStatusContextoId]
  );
  const xFluxoContextoEfetivo = useMemo(
    () => pvResolverFluxoEfetivo(xStatusContextoId, xAvaliacaoTokenRequestOptions),
    [xAvaliacaoTokenRequestOptions, xStatusContextoId]
  );

  const pvLimparQuestionario = useCallback(() => {
    setModeloQuestoes([]);
    setAnexosAvaliacao([]);
    setRespostasGestor({});
  }, []);

  const pvAtualizarEstadoSalvamento = useCallback(() => {
    setAvaliacaoSalvando(xUpsertsPendentesRef.current > 0);
  }, []);

  const pvFocarOtp = useCallback(pIndex => {
    xOtpInputRefs.current?.[pIndex]?.focus?.();
  }, []);

  const pvAtualizarOtpPorIndice = useCallback(
    (pIndex, pValor) => {
      const xOtpAtual = Array.from(
        { length: wOtpGestorAcessoTamanho },
        (_pItem, pItemIndex) => otpCodigo?.[pItemIndex] || ''
      );
      const xValorNormalizado = normalizarOtp(pValor);

      if (!xValorNormalizado) {
        xOtpAtual[pIndex] = '';
        return xOtpAtual.join('');
      }

      xValorNormalizado.split('').forEach((pDigito, pOffset) => {
        const xTargetIndex = pIndex + pOffset;

        if (xTargetIndex < wOtpGestorAcessoTamanho) {
          xOtpAtual[xTargetIndex] = pDigito;
        }
      });

      return xOtpAtual.join('');
    },
    [otpCodigo]
  );

  const pvIniciarSalvamentoResposta = useCallback(() => {
    xUpsertsPendentesRef.current += 1;
    pvAtualizarEstadoSalvamento();
  }, [pvAtualizarEstadoSalvamento]);

  const pvFinalizarSalvamentoResposta = useCallback(() => {
    xUpsertsPendentesRef.current = Math.max(0, xUpsertsPendentesRef.current - 1);
    pvAtualizarEstadoSalvamento();
  }, [pvAtualizarEstadoSalvamento]);

  const pvAtualizarDataUltimaAtualizacao = useCallback(pData => {
    const xNovaDataAtualizacao = pData?.modified || pData?.updated_at || new Date().toISOString();

    setAvaliacao(pPrev =>
      pPrev
        ? {
            ...pPrev,
            modified: xNovaDataAtualizacao,
            updated_at: xNovaDataAtualizacao
          }
        : pPrev
    );
  }, []);

  const pvCarregarAnexosAvaliacao = useCallback(
    (pAvaliacaoId, pRequestOptions = {}) => {
      if (!pAvaliacaoId) {
        setAnexosAvaliacao([]);
        return;
      }

      services.avaliacoes.listUpload(
        pAvaliacaoId,
        rRes => {
          setAnexosAvaliacao(
            Array.isArray(rRes?.data)
              ? [...rRes.data].sort((pAnexoA, pAnexoB) => {
                  return new Date(pAnexoA?.created || 0) - new Date(pAnexoB?.created || 0);
                })
              : []
          );
        },
        () => {
          setAnexosAvaliacao([]);
          onMessageError?.('Não foi possível carregar os anexos da avaliação.');
        },
        undefined,
        pRequestOptions
      );
    },
    [onMessageError]
  );

  const pvCarregarRespostasAvaliacao = useCallback(
    (pRequestOptions = {}) => {
      setTelaCarregando(true);

      services.respostas.list(
        { avaliacao_id: paramsId },
        rRespostasRes => {
          const xAvaliacaoPayload = rRespostasRes?.data || null;
          const xQuestoesModelo = Array.isArray(xAvaliacaoPayload?.respostas)
            ? xAvaliacaoPayload.respostas.filter(pQuestao => pQuestao?.resposta_tipo != null)
            : [];
          const xRespostasHydratadas = extrairRespostasGestorPorAvaliacao(
            xQuestoesModelo,
            xQuestoesModelo
          );
          const xFluxo = pvResolverFluxoEfetivo(xAvaliacaoPayload?.status, pRequestOptions);

          setOtpCodigo('');
          setOtpStatus(pvCriarOtpStatusInicial());
          setAvaliacao(xAvaliacaoPayload);
          setModeloQuestoes(xQuestoesModelo);
          setRespostasGestor(xRespostasHydratadas);
          setFluxoEntrada(xFluxo);
          setFluxoAcao('idle');
          setTelaCarregando(false);

          if (xFluxo === wFluxoEntradaAvaliacao.questionario) {
            pvCarregarAnexosAvaliacao(xAvaliacaoPayload?.avaliacao_id, pRequestOptions);
            return;
          }

          setAnexosAvaliacao([]);
        },
        rErro => {
          setTelaCarregando(false);
          setAvaliacao(null);
          pvLimparQuestionario();
          setFluxoEntrada(wFluxoEntradaAvaliacao.questionario);
          setFluxoAcao('idle');

          if (
            !pRequestOptions?.authToken &&
            hasResolvedAvaliacaoStatus &&
            xStatusContextoId !== null &&
            xFluxoContexto === wFluxoEntradaAvaliacao.gate &&
            isErroAcessoNegado(rErro)
          ) {
            setOtpCodigo('');
            setOtpStatus({
              etapa: wOtpGestorEtapas.solicitar,
              modo: wOtpGestorModos.idle,
              erro: '',
              cooldownEndsAt: null
            });
            return;
          }

          onMessageError?.('Não foi possível carregar as respostas da avaliação.');
        },
        undefined,
        pRequestOptions
      );
    },
    [
      hasResolvedAvaliacaoStatus,
      onMessageError,
      paramsId,
      pvCarregarAnexosAvaliacao,
      pvLimparQuestionario,
      xFluxoContexto,
      xStatusContextoId
    ]
  );

  useEffect(() => {
    if (!paramsId) {
      return;
    }

    setOtpCodigo('');
    setOtpStatus(pvCriarOtpStatusInicial());
    setAvaliacaoToken('');
    setAvaliacao(null);
    xUpsertsPendentesRef.current = 0;
    setAvaliacaoSalvando(false);
    setModeloQuestoes([]);
    setFluxoEntrada(wFluxoEntradaAvaliacao.questionario);
    setFluxoAcao('idle');
    setAssinaturaEntregaStatus(wStatusEntregaAssinatura.confirmacao);
    setAssinaturaEntregaErro('');
    setAnexosAvaliacao([]);
    setRespostasGestor({});
    xUltimaHidratacaoRef.current = '';
  }, [paramsId]);

  useEffect(() => {
    if (!paramsId) {
      return;
    }

    if (!hasResolvedAvaliacaoStatus || isLoadingAvaliacaoStatus) {
      setTelaCarregando(true);
      return;
    }

    const xDeveHidratarOtpQuery =
      xFluxoContextoEfetivo === wFluxoEntradaAvaliacao.gate &&
      !xAvaliacaoTokenRequestOptions?.authToken &&
      Boolean(xOtpQueryParam);

    setOtpCodigo(xDeveHidratarOtpQuery ? xOtpQueryParam : '');
    setOtpStatus(
      xDeveHidratarOtpQuery ? pvCriarOtpStatusVerificacaoInicial() : pvCriarOtpStatusInicial()
    );
    setAvaliacao(pPrev => ({
      ...(pPrev || {}),
      avaliacao_id: pPrev?.avaliacao_id || paramsId,
      status: xStatusContextoId
    }));
    setFluxoEntrada(xFluxoContextoEfetivo);
    setFluxoAcao('idle');

    if (xStatusContextoId === null) {
      pvLimparQuestionario();
      setTelaCarregando(false);
      return;
    }

    if (xFluxoContextoEfetivo !== wFluxoEntradaAvaliacao.questionario) {
      xUltimaHidratacaoRef.current = '';
      pvLimparQuestionario();
      setTelaCarregando(false);
      return;
    }

    const xChaveHidratacao = `${paramsId}:${xStatusContextoId || ''}:${xAvaliacaoTokenRequestOptions.authToken || ''}`;

    if (xUltimaHidratacaoRef.current === xChaveHidratacao) {
      setTelaCarregando(false);
      return;
    }

    xUltimaHidratacaoRef.current = xChaveHidratacao;
    pvCarregarRespostasAvaliacao(xAvaliacaoTokenRequestOptions);
  }, [
    hasResolvedAvaliacaoStatus,
    isLoadingAvaliacaoStatus,
    paramsId,
    pvCarregarRespostasAvaliacao,
    pvLimparQuestionario,
    xAvaliacaoTokenRequestOptions,
    xFluxoContextoEfetivo,
    xOtpQueryParam,
    xStatusContextoId
  ]);

  useEffect(() => {
    if (otpStatus.etapa !== wOtpGestorEtapas.verificar) {
      return;
    }

    pvFocarOtp(0);
  }, [otpStatus.etapa, pvFocarOtp]);

  useEffect(() => {
    if (!otpStatus.cooldownEndsAt) {
      return undefined;
    }

    if (otpStatus.cooldownEndsAt <= Date.now()) {
      setOtpStatus(pPrev => {
        if (!pPrev.cooldownEndsAt || pPrev.cooldownEndsAt > Date.now()) {
          return pPrev;
        }

        return {
          ...pPrev,
          cooldownEndsAt: null
        };
      });

      return undefined;
    }

    const xInterval = window.setInterval(() => {
      setOtpStatus(pPrev => {
        if (!pPrev.cooldownEndsAt) {
          return pPrev;
        }

        if (pPrev.cooldownEndsAt <= Date.now()) {
          return {
            ...pPrev,
            cooldownEndsAt: null
          };
        }

        return { ...pPrev };
      });
    }, 1000);

    return () => {
      window.clearInterval(xInterval);
    };
  }, [otpStatus.cooldownEndsAt]);

  const xSessoesQuestoes = xHydratacao.sessoesQuestoes;
  const xSessoes = xHydratacao.sessoes;

  useEffect(() => {
    if (!xTemQuestoesDoModelo) {
      return;
    }

    const xPrimeiraSessao = Object.keys(xSessoes)[0];

    if (!xPrimeiraSessao) {
      return;
    }

    if (!xSessoes[activeCategoryId]) {
      actions.handleChangeActiveSessaoId(xPrimeiraSessao);
      const xPrimeiraQuestao = xSessoesQuestoes[xPrimeiraSessao]?.questoes?.[0];

      if (xPrimeiraQuestao?.id) {
        actions.handleChangeActiveQuestionId(xPrimeiraQuestao.id);
      }

      return;
    }

    const xQuestoesSessaoAtiva = xSessoesQuestoes[activeCategoryId]?.questoes || [];
    const xQuestaoAtivaExiste = xQuestoesSessaoAtiva.some(
      pQuestao => pQuestao.id === activeQuestionId
    );

    if (!xQuestaoAtivaExiste) {
      const xPrimeiraQuestaoAtiva = xQuestoesSessaoAtiva[0];

      if (xPrimeiraQuestaoAtiva?.id) {
        actions.handleChangeActiveQuestionId(xPrimeiraQuestaoAtiva.id);
      }
    }
  }, [
    actions,
    activeCategoryId,
    activeQuestionId,
    xSessoes,
    xSessoesQuestoes,
    xTemQuestoesDoModelo
  ]);

  const handleSelectCategoria = useCallback(
    (_pEvent, pSessao) => {
      pSessao.id && actions.handleChangeActiveSessaoId(pSessao.id);
      const xFirstQuestionId = xSessoesQuestoes[pSessao.id]?.questoes?.[0]?.id;
      xFirstQuestionId && actions.handleChangeActiveQuestionId(xFirstQuestionId);
    },
    [actions, xSessoesQuestoes]
  );

  const handleOpenAnexos = useCallback(() => {
    xInputAnexosRef.current?.click();
  }, []);

  const handleAddAnexos = useCallback(
    pEvent => {
      const xFiles = Array.from(pEvent.target.files || []);

      if (!xFiles.length) {
        return;
      }

      if (avaliacao?.avaliacao_id) {
        services.avaliacoes.upload(
          {
            avaliacao_id: avaliacao.avaliacao_id,
            documento_tipo: 'EVIDENCIA',
            files: xFiles
          },
          () => {
            pvCarregarAnexosAvaliacao(avaliacao.avaliacao_id, xAvaliacaoTokenRequestOptions);
          },
          () => {
            onMessageError?.('Não foi possível anexar arquivo(s) da avaliação.');
          },
          undefined,
          xAvaliacaoTokenRequestOptions
        );
      }

      pEvent.target.value = '';
    },
    [onMessageError, pvCarregarAnexosAvaliacao, avaliacao, xAvaliacaoTokenRequestOptions]
  );

  const xQuestoes = useMemo(
    () => Object.keys(xSessoes).flatMap(pSessaoId => xSessoesQuestoes[pSessaoId]?.questoes || []),
    [xSessoes, xSessoesQuestoes]
  );

  const xQuestoesById = useMemo(
    () =>
      xQuestoes.reduce((pAcc, pQuestao) => {
        pAcc[pQuestao.id] = pQuestao;
        return pAcc;
      }, {}),
    [xQuestoes]
  );

  const xRespondidas = useMemo(
    () =>
      xQuestoes.reduce((pCount, pQuestao) => {
        return temValorResposta(respostasGestor?.[pQuestao.id]?.resposta_id)
          ? pCount + 1
          : pCount;
      }, 0),
    [xQuestoes, respostasGestor]
  );

  const xSessoesProgress = useMemo(
    () =>
      Object.fromEntries(
        Object.values(xSessoes).map(xSessao => {
          const xQuestoesSessao = xSessoesQuestoes[xSessao.id]?.questoes || [];
          const xTotalSessao = xQuestoesSessao.length;
          const xRespondidasSessao = xQuestoesSessao.reduce((pCount, pQuestao) => {
            return temValorResposta(respostasGestor?.[pQuestao.id]?.resposta_id)
              ? pCount + 1
              : pCount;
          }, 0);
          const xProgression = xTotalSessao ? (xRespondidasSessao / xTotalSessao) * 100 : 0;

          return [
            xSessao.id,
            {
              ...xSessao,
              progression: xProgression,
              completed: xTotalSessao > 0 && xRespondidasSessao >= xTotalSessao
            }
          ];
        })
      ),
    [xSessoes, xSessoesQuestoes, respostasGestor]
  );

  const { anexosAgrupados: xAnexosAgrupados, anexosQuestoes: xAnexosQuestoes } = useMemo(
    () => agruparAnexos(anexosAvaliacao, respostasGestor),
    [anexosAvaliacao, respostasGestor]
  );

  const handleToggleGrupoQuestao = useCallback(pQuestionId => {
    setGruposQuestoesAbertos(pPrev => ({
      ...pPrev,
      [pQuestionId]: pPrev?.[pQuestionId] === false ? true : false
    }));
  }, []);

  const xTotalAnexos = anexosAvaliacao.length + xAnexosQuestoes.length;

  const pvUpsertRespostaDebounced = useCallback(
    (pQuestionId, pPayload) => {
      if (!xDebouncedUpsertByQuestaoRef.current[pQuestionId]) {
        xDebouncedUpsertByQuestaoRef.current[pQuestionId] = utils.helpers.debounce(pData => {
          pvIniciarSalvamentoResposta();
          services.respostas.upsert(
            normalizarRespostaPayloadParaUpsert(pData),
            rRes => {
              pvAtualizarDataUltimaAtualizacao(rRes?.data);
            },
            () => {
              onMessageError?.('Não foi possível salvar a resposta da questão.');
            },
            () => {
              pvFinalizarSalvamentoResposta();
            },
            xAvaliacaoTokenRequestOptions
          );
        }, 500);
      }

      xDebouncedUpsertByQuestaoRef.current[pQuestionId](pPayload);
    },
    [
      onMessageError,
      pvAtualizarDataUltimaAtualizacao,
      pvFinalizarSalvamentoResposta,
      pvIniciarSalvamentoResposta,
      xAvaliacaoTokenRequestOptions
    ]
  );

  const handleQuestionChange = useCallback(
    (pQuestionId, pData = {}) => {
      setRespostasGestor(pPrev => {
        const { anexos_novos: _xAnexosNovosDescartar, ...xDataPersistir } = pData;
        const xNovaResposta = {
          ...(pPrev?.[pQuestionId] || {}),
          ...xDataPersistir
        };
        const xQuestao = xQuestoesById?.[pQuestionId];
        const xAvaliacaoId = avaliacao?.avaliacao_id;
        const xRespostaValor = extrairValorResposta(xQuestao, xNovaResposta);
        const xAnexosNovos = Array.isArray(pData?.anexos_novos) ? pData.anexos_novos : [];

        if (xAnexosNovos.length > 0) {
          xNovaResposta.anexos_uploading = true;
        }

        if (xQuestao && xAvaliacaoId && xAnexosNovos.length > 0) {
          services.avaliacoes.upload(
            {
              avaliacao_id: xAvaliacaoId,
              avaliacao_questao_id: Number(pQuestionId),
              documento_tipo: 'EVIDENCIA',
              files: xAnexosNovos
            },
            () => {
              setRespostasGestor(pRespostaPrev => {
                const xRespostaAtual = pRespostaPrev?.[pQuestionId] || {};

                return {
                  ...pRespostaPrev,
                  [pQuestionId]: {
                    ...xRespostaAtual,
                    anexos_uploading: false,
                    anexos: [],
                    anexos_novos: []
                  }
                };
              });
              pvCarregarAnexosAvaliacao(xAvaliacaoId, xAvaliacaoTokenRequestOptions);
            },
            () => {
              setRespostasGestor(pRespostaPrev => {
                const xRespostaAtual = pRespostaPrev?.[pQuestionId] || {};

                return {
                  ...pRespostaPrev,
                  [pQuestionId]: {
                    ...xRespostaAtual,
                    anexos_uploading: false,
                    anexos: [],
                    anexos_novos: []
                  }
                };
              });
              onMessageError?.('Não foi possível anexar arquivo(s) da questão.');
            },
            undefined,
            xAvaliacaoTokenRequestOptions
          );
        }

        if (xQuestao && xAvaliacaoId && temValorResposta(xRespostaValor)) {
          pvUpsertRespostaDebounced(pQuestionId, {
            avaliacao_id: xAvaliacaoId,
            avaliacao_questao_id: Number(pQuestionId),
            resposta: xRespostaValor,
            comentario: xNovaResposta?.comentario || ''
          });
        }

        return {
          ...pPrev,
          [pQuestionId]: xNovaResposta
        };
      });
    },
    [
      onMessageError,
      pvCarregarAnexosAvaliacao,
      pvUpsertRespostaDebounced,
      avaliacao,
      xAvaliacaoTokenRequestOptions,
      xQuestoesById
    ]
  );

  const xTodasRespondidas = xQuestoes.length > 0 && xRespondidas >= xQuestoes.length;

  const handleOpenAssinaturaDialog = useCallback(() => {
    setAssinaturaEntregaStatus(wStatusEntregaAssinatura.confirmacao);
    setAssinaturaEntregaErro('');
    setAssinaturaDialogOpen(true);
  }, []);

  const handleCloseAssinaturaDialog = useCallback(() => {
    if (xEntregaEmAndamento) {
      return;
    }

    setAssinaturaDialogOpen(false);
    setAssinaturaConfirmada(false);
    setAssinaturaEntregaStatus(wStatusEntregaAssinatura.confirmacao);
    setAssinaturaEntregaErro('');
  }, [xEntregaEmAndamento]);

  const handleChangeConfirmacaoEntrega = useCallback(pConfirmada => {
    setAssinaturaConfirmada(pConfirmada);
    setAssinaturaEntregaErro('');
  }, []);

  const handleOpenRemoveAnexoDialog = useCallback(pAnexo => {
    if (!pAnexo?.avaliacao_upload_id) {
      return;
    }

    setAnexoRemocaoPendente(pAnexo);
  }, []);

  const handleCloseRemoveAnexoDialog = useCallback(() => {
    setAnexoRemocaoPendente(null);
  }, []);

  const handleRemoveAnexo = useCallback(() => {
    const pAnexo = anexoRemocaoPendente;
    const xAvaliacaoId = avaliacao?.avaliacao_id;
    const xAvaliacaoUploadId = pAnexo?.avaliacao_upload_id;

    if (!xAvaliacaoId || !xAvaliacaoUploadId) {
      return;
    }

    services.avaliacoes.removeUpload(
      {
        avaliacao_id: xAvaliacaoId,
        avaliacao_upload_id: xAvaliacaoUploadId
      },
      () => {
        handleCloseRemoveAnexoDialog();
        pvCarregarAnexosAvaliacao(xAvaliacaoId, xAvaliacaoTokenRequestOptions);
      },
      () => {
        handleCloseRemoveAnexoDialog();
        onMessageError?.('Não foi possível remover o anexo.');
      },
      undefined,
      xAvaliacaoTokenRequestOptions
    );
  }, [
    handleCloseRemoveAnexoDialog,
    onMessageError,
    pvCarregarAnexosAvaliacao,
    anexoRemocaoPendente,
    avaliacao,
    xAvaliacaoTokenRequestOptions
  ]);

  const handleChangeOtp = useCallback(
    (pIndex, pEvent) => {
      const xValorNormalizado = normalizarOtp(pEvent?.target?.value);
      const xNovoCodigo = pvAtualizarOtpPorIndice(pIndex, xValorNormalizado);

      setOtpCodigo(xNovoCodigo);
      setOtpStatus(pPrev => ({
        ...pPrev,
        erro: ''
      }));

      if (!xValorNormalizado) {
        return;
      }

      pvFocarOtp(Math.min(pIndex + xValorNormalizado.length, wOtpGestorAcessoTamanho - 1));
    },
    [pvAtualizarOtpPorIndice, pvFocarOtp]
  );

  const handleClearOtp = useCallback(() => {
    setOtpCodigo('');
    setOtpStatus(pPrev => ({
      ...pPrev,
      erro: ''
    }));
    pvFocarOtp(0);
  }, [pvFocarOtp]);

  const pvEmitirOtp = useCallback(
    pMensagemErro => {
      if (!paramsId) {
        return;
      }

      setOtpStatus(pPrev => ({
        ...pPrev,
        etapa: wOtpGestorEtapas.verificar,
        modo: wOtpGestorModos.emitindo,
        erro: ''
      }));

      services.avaliacoes.otpEmitir(
        {
          avaliacao_id: paramsId
        },
        () => {
          setOtpStatus(pPrev => ({
            ...pPrev,
            etapa: wOtpGestorEtapas.verificar,
            modo: wOtpGestorModos.idle,
            erro: '',
            cooldownEndsAt: Date.now() + wOtpGestorCooldownMs
          }));
          onMessageSuccess?.('Código de acesso enviado para o e-mail do gestor.');
        },
        () => {
          setOtpStatus(pPrev => ({
            ...pPrev,
            etapa: wOtpGestorEtapas.verificar,
            modo: wOtpGestorModos.idle,
            erro: pMensagemErro
          }));
        }
      );
    },
    [onMessageSuccess, paramsId]
  );

  const handleSolicitarOtp = useCallback(() => {
    if (!paramsId) {
      return;
    }

    setOtpCodigo('');
    setOtpStatus({
      etapa: wOtpGestorEtapas.verificar,
      modo: wOtpGestorModos.idle,
      erro: '',
      cooldownEndsAt: null
    });
    pvEmitirOtp('Não foi possível enviar o código. Tente novamente.');
  }, [paramsId, pvEmitirOtp]);

  const handleReenviarOtp = useCallback(() => {
    if (!xOtpPodeReenviar) {
      return;
    }

    pvEmitirOtp('Não foi possível reenviar o código. Tente novamente.');
  }, [pvEmitirOtp, xOtpPodeReenviar]);

  const handlePasteOtp = useCallback(
    (pIndex, pEvent) => {
      pEvent?.preventDefault?.();

      const xTextoColado = normalizarOtp(pEvent?.clipboardData?.getData('text'));

      if (!xTextoColado) {
        return;
      }

      setOtpCodigo(pvAtualizarOtpPorIndice(pIndex, xTextoColado));
      setOtpStatus(pPrev => ({
        ...pPrev,
        erro: ''
      }));
      pvFocarOtp(Math.min(pIndex + xTextoColado.length - 1, wOtpGestorAcessoTamanho - 1));
    },
    [pvAtualizarOtpPorIndice, pvFocarOtp]
  );

  const handleKeyDownOtp = useCallback(
    (pIndex, pEvent) => {
      if (pEvent?.key === 'ArrowLeft') {
        pEvent.preventDefault();
        pvFocarOtp(Math.max(0, pIndex - 1));
        return;
      }

      if (pEvent?.key === 'ArrowRight') {
        pEvent.preventDefault();
        pvFocarOtp(Math.min(wOtpGestorAcessoTamanho - 1, pIndex + 1));
        return;
      }

      if (pEvent?.key !== 'Backspace') {
        return;
      }

      pEvent.preventDefault();

      const xOtpAtual = Array.from(
        { length: wOtpGestorAcessoTamanho },
        (_pItem, pItemIndex) => otpCodigo?.[pItemIndex] || ''
      );

      if (xOtpAtual[pIndex]) {
        xOtpAtual[pIndex] = '';
        setOtpCodigo(xOtpAtual.join(''));
        setOtpStatus(pPrev => ({
          ...pPrev,
          erro: ''
        }));
        return;
      }

      const xIndexAnterior = Math.max(0, pIndex - 1);
      xOtpAtual[xIndexAnterior] = '';
      setOtpCodigo(xOtpAtual.join(''));
      setOtpStatus(pPrev => ({
        ...pPrev,
        erro: ''
      }));
      pvFocarOtp(xIndexAnterior);
    },
    [otpCodigo, pvFocarOtp]
  );

  const handleSubmitOtp = useCallback(
    pEvent => {
      pEvent?.preventDefault?.();

      if (!paramsId) {
        return;
      }

      if (otpCodigo.length !== wOtpGestorAcessoTamanho) {
        setOtpStatus(pPrev => ({
          ...pPrev,
          erro: 'Informe o código de 6 dígitos.'
        }));
        return;
      }

      setOtpStatus(pPrev => ({
        ...pPrev,
        modo: wOtpGestorModos.validando,
        erro: ''
      }));

      services.avaliacoes.otpValidar(
        {
          avaliacao_id: paramsId,
          otp: Number(otpCodigo)
        },
        rRes => {
          const xNovoToken = rRes?.data?.avaliacao_token || '';

          if (!xNovoToken) {
            setOtpStatus(pPrev => ({
              ...pPrev,
              modo: wOtpGestorModos.idle,
              erro: 'Não foi possível validar o código informado.'
            }));
            return;
          }

          setAvaliacaoToken(xNovoToken);
          setOtpStatus(pPrev => ({
            ...pPrev,
            modo: wOtpGestorModos.idle,
            erro: ''
          }));
          onOtpValidated?.();
          setTelaCarregando(true);
          onRefreshAvaliacaoStatus?.(paramsId);
        },
        () => {
          setOtpStatus(pPrev => ({
            ...pPrev,
            modo: wOtpGestorModos.idle,
            erro: 'Código inválido. Tente novamente.'
          }));
        }
      );
    },
    [onOtpValidated, onRefreshAvaliacaoStatus, otpCodigo, paramsId]
  );

  const handleAceitarQuestionario = useCallback((pCallbacks = {}) => {
    const xAvaliacaoId = avaliacao?.avaliacao_id || paramsId;
    const { onSuccess, onError } = pCallbacks;

    if (!xAvaliacaoId || xFluxoAcaoEmAndamento) {
      return;
    }

    setFluxoAcao('aceitando');
    setTelaCarregando(true);

    services.avaliacoes.responder(
      {
        avaliacao_id: xAvaliacaoId
      },
      rRes => {
        onSuccess?.(rRes);
      },
      () => {
        setTelaCarregando(false);
        setFluxoAcao('idle');
        onError?.();
        onMessageError?.('Não foi possível iniciar o questionário da avaliação.');
      },
      undefined,
      xAvaliacaoTokenRequestOptions
    );
  }, [
    onMessageError,
    paramsId,
    avaliacao,
    xAvaliacaoTokenRequestOptions,
    xFluxoAcaoEmAndamento
  ]);

  const handleRecusarQuestionario = useCallback((pCallbacks = {}) => {
    const xAvaliacaoId = avaliacao?.avaliacao_id || paramsId;
    const { onSuccess, onError } = pCallbacks;

    if (!xAvaliacaoId || xFluxoAcaoEmAndamento) {
      return;
    }

    setFluxoAcao('recusando');
    setTelaCarregando(true);

    services.avaliacoes.recusar(
      {
        avaliacao_id: xAvaliacaoId
      },
      rRes => {
        onSuccess?.(rRes);
      },
      () => {
        setTelaCarregando(false);
        setFluxoAcao('idle');
        onError?.();
        onMessageError?.('Não foi possível encerrar a avaliação.');
      },
      undefined,
      xAvaliacaoTokenRequestOptions
    );
  }, [
    onMessageError,
    paramsId,
    avaliacao,
    xAvaliacaoTokenRequestOptions,
    xFluxoAcaoEmAndamento
  ]);

  const handleFinalizarEntrega = useCallback(() => {
    const xAvaliacaoId = avaliacao?.avaliacao_id;

    if (!assinaturaConfirmada || !xAvaliacaoId || xFluxoAcaoEmAndamento || xEntregaEmAndamento) {
      return;
    }

    setAssinaturaEntregaErro('');
    setAssinaturaEntregaStatus(wStatusEntregaAssinatura.enviando);
    setFluxoAcao('finalizando-entrega');

    services.avaliacoes.responderFinalizar(
      {
        avaliacao_id: xAvaliacaoId
      },
      () => {
        setTelaCarregando(true);
        setFluxoAcao('idle');
        setAssinaturaEntregaStatus(wStatusEntregaAssinatura.confirmacao);
        setAssinaturaEntregaErro('');
        setAssinaturaConfirmada(false);
        setAssinaturaDialogOpen(false);
        onRefreshAvaliacaoStatus?.(xAvaliacaoId);
      },
      () => {
        setFluxoAcao('idle');
        setAssinaturaEntregaStatus(wStatusEntregaAssinatura.confirmacao);
        setAssinaturaEntregaErro(
          'Não foi possível concluir o envio. Revise as informações e tente novamente.'
        );
        onMessageError?.('Não foi possível entregar o questionário.');
      },
      undefined,
      xAvaliacaoTokenRequestOptions
    );
  }, [
    assinaturaConfirmada,
    onRefreshAvaliacaoStatus,
    onMessageError,
    avaliacao,
    xAvaliacaoTokenRequestOptions,
    xEntregaEmAndamento,
    xFluxoAcaoEmAndamento
  ]);

  return {
    avaliacaoToken,
    page: {
      title: xTituloAvaliacao,
      subtitle: 'Avaliação',
      description: xDescricaoAvaliacao
    },
    flow: {
      entrada: fluxoEntrada,
      telaCarregando,
      acaoEmAndamento: xFluxoAcaoEmAndamento,
      otpNecessario: xOtpNecessario
    },
    otp: {
      codigo: otpCodigo,
      digitos: xOtpDigitos,
      status: otpStatus,
      cooldownAtivo: xOtpCooldownAtivo,
      cooldownRestanteMs: xOtpCooldownRestanteMs,
      podeValidar: xOtpPodeValidar,
      podeReenviar: xOtpPodeReenviar,
      inputRefs: xOtpInputRefs,
      formatarCooldown: formatarCooldownOtp
    },
    questionario: {
      avaliacao,
      avaliacaoSalvando,
      sessoes: xSessoes,
      sessoesQuestoes: xSessoesQuestoes,
      sessoesProgress: xSessoesProgress,
      respostas: respostasGestor,
      mostrarApenasNaoRespondidas,
      questoesTotal: xQuestoes.length,
      respondidas: xRespondidas,
      todasRespondidas: xTodasRespondidas,
      activeCategoryId,
      activeQuestionId
    },
    anexos: {
      avaliacao: anexosAvaliacao,
      questoes: xAnexosQuestoes,
      agrupados: xAnexosAgrupados,
      gruposAbertos: gruposQuestoesAbertos,
      total: xTotalAnexos,
      inputRef: xInputAnexosRef
    },
    dialogs: {
      assinaturaOpen: assinaturaDialogOpen,
      assinaturaConfirmada,
      assinaturaEntregaStatus,
      assinaturaEntregaErro,
      assinaturaEntregando: xEntregaEmAndamento,
      anexoRemocaoPendente
    },
    actions: {
      handleSelectCategoria,
      handleQuestionChange,
      setMostrarApenasNaoRespondidas,
      handleOpenAnexos,
      handleAddAnexos,
      handleToggleGrupoQuestao,
      handleOpenAssinaturaDialog,
      handleCloseAssinaturaDialog,
      handleChangeConfirmacaoEntrega,
      handleFinalizarEntrega,
      handleOpenRemoveAnexoDialog,
      handleCloseRemoveAnexoDialog,
      handleRemoveAnexo,
      handleChangeOtp,
      handleClearOtp,
      handleSolicitarOtp,
      handleReenviarOtp,
      handlePasteOtp,
      handleKeyDownOtp,
      handleSubmitOtp,
      handleAceitarQuestionario,
      handleRecusarQuestionario,
      handleChangeActiveQuestionId: actions.handleChangeActiveQuestionId
    }
  };
}
